"use client"

import { useCallback, useRef } from "react"

let lambdaWarmedAt = 0
const WARM_DURATION_MS = 4 * 60 * 1000 // 4 mins (Lambda warm period)
const HOVER_DELAY_MS = 50 // Only warm if hover > 50ms

/**
 * Hook to warm Lambda on hover for faster page loads.
 * - Only warms Lambda, does NOT prefetch HTML (avoids stale data issues)
 * - Rate-limited: only 1 warm request per 4 minutes globally
 * - Debounced: only fires if hover > 50ms
 */
export const useWarmOnHover = (url: string) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const onMouseEnter = useCallback(() => {
    if (!url || url.startsWith("#")) return

    timeoutRef.current = setTimeout(() => {
      const now = Date.now()
      if (now - lambdaWarmedAt >= WARM_DURATION_MS) {
        lambdaWarmedAt = now
        fetch(url, { method: "HEAD", credentials: "include" }).catch(() => {})
      }
    }, HOVER_DELAY_MS)
  }, [url])

  const onMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  return { onMouseEnter, onMouseLeave }
}
